import React, { useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { useDispatch } from 'react-redux';
import startCase from 'lodash/startCase';
import { AppliedFilters, tailwindOverride } from '@getsynapse/design-system';
import {
  RequestFilters,
  RequestFiltersKey,
  AppliedFilter,
  RangeFilter,
} from 'utils/types/filters';
import { isDateRangeFilter } from 'utils/typeGuards';
import { DATE, REQUEST_TABLE_FILTERS } from 'utils/constants';
import useRequestFilters from '../../hooks/useRequestFilters';
import { getUserSummaries } from 'state/normalized/userSummaries/userSummariesSlice';
import { getProjectSummaries } from 'state/normalized/projectSummaries/projectSummariesSlice';
import { getBusinessUnitSummaries } from 'state/normalized/businessUnitSummaries/businessUnitSummariesSlice';
import { getFormSummaries } from 'state/normalized/formSummaries/formSummariesSlice';

const RequestsAppliedFiltersTags: React.FC<{
  filters: RequestFilters;
  onUpdateFilters: (filters: RequestFilters) => void;
  onDisplayAllFilters: () => void;
  onRemoveFilter: (filter: AppliedFilter) => void;
}> = ({ filters, onUpdateFilters, onDisplayAllFilters, onRemoveFilter }) => {
  const dispatch = useDispatch();

  const { getDateFilterLabel, getFilterOptionLabelByKeyAndValue } =
    useRequestFilters();

  const appliedFilters = useMemo<AppliedFilter[]>(() => {
    const tags: AppliedFilter[] = [];
    for (const key of Object.keys(filters)) {
      const filterKey = key as RequestFiltersKey;
      if (isDateRangeFilter(filters[filterKey])) {
        const filterValue = filters[filterKey] as RangeFilter;
        const label = getDateFilterLabel(filterValue, DATE.TASK_TABLE_FORMAT);
        tags.push({ label, value: '', key });
      } else if (typeof filters[filterKey] !== 'boolean') {
        const filterValues = filters[filterKey] as string[];
        filterValues.forEach((value: string) => {
          const label = getFilterOptionLabelByKeyAndValue(filterKey, value);
          tags.push({ label, value, key });
        });
      } else if (filterKey === REQUEST_TABLE_FILTERS.ARCHIVED) {
        const filterValue = filters[filterKey] as boolean;
        tags.push({ label: startCase(filterKey), value: filterValue, key });
      }
    }
    return tags;
  }, [filters, getDateFilterLabel, getFilterOptionLabelByKeyAndValue]);

  useEffect(() => {
    const requesterIds = filters[REQUEST_TABLE_FILTERS.REQUESTER] as string[];
    const ownerIds = filters[REQUEST_TABLE_FILTERS.OWNERS] as string[];
    const projectIds = filters[REQUEST_TABLE_FILTERS.PROJECTS] as string[];
    const businessUnitIds = filters[
      REQUEST_TABLE_FILTERS.BUSINESS_TEAMS
    ] as string[];
    const formIds = filters[REQUEST_TABLE_FILTERS.FORM] as string[];

    const uniqueUserIds = Array.from(
      new Set([...(ownerIds || []), ...(requesterIds || [])])
    );
    if (uniqueUserIds.length) {
      dispatch(
        getUserSummaries({
          userIds: uniqueUserIds,
        })
      );
    }

    if (projectIds?.length) {
      dispatch(
        getProjectSummaries({
          projectIds,
        })
      );
    }

    if (businessUnitIds?.length) {
      dispatch(
        getBusinessUnitSummaries({
          businessUnitIds,
        })
      );
    }
    if (formIds?.length) {
      dispatch(
        getFormSummaries({
          formIds,
        })
      );
    }
  }, [dispatch, filters]);

  const clearStandardFilters = () =>
    onUpdateFilters({
      [REQUEST_TABLE_FILTERS.MY_REQUESTS]:
        filters[REQUEST_TABLE_FILTERS.MY_REQUESTS],
      [REQUEST_TABLE_FILTERS.OPEN_REQUESTS]:
        filters[REQUEST_TABLE_FILTERS.OPEN_REQUESTS],
    });

  return (
    <AppliedFilters<AppliedFilter>
      filters={appliedFilters}
      onRemoveFilter={onRemoveFilter}
      onClearAll={clearStandardFilters}
      onDisplayAllFilters={onDisplayAllFilters}
      className={tailwindOverride('border-primary-lighter-two z-20')}
      data-testid='applied-requests-filters-tags'
      title={intl.get('FILTER_PANEL.FILTERS_APPLIED')}
      clearAllLabel={intl.get('FILTER_PANEL.CLEAR_ALL')}
    />
  );
};

export default RequestsAppliedFiltersTags;
